import { ColumnDef } from "@tanstack/react-table";
import { Check, Cloud, Upload, X } from "lucide-react";
import { useMemo } from "react";
import { FileStatusItem } from "../../api/services/uploadService";
import {
  AnimatedPage,
  FadeIn,
  StaggerChildren,
} from "../../components/AnimatedPage";
import { DataTable } from "../../components/ui/data-table";
import { ProgressCircle } from "../../components/ui/progress-circle";
import { StatBoxSkeleton } from "../../components/ui/skeleton-loaders";
import { StatBox } from "../../components/ui/stat-box";
import { UploadBox } from "../../components/ui/upload-box";
import { useFileStatus } from "./hooks/useFileStatus";

interface UploadConfig {
  title: string;
  subtitle: string;
  docType: string;
  extensionFile: string;
  uploadTitle: string;
}

const columns: ColumnDef<FileStatusItem>[] = [
  {
    accessorKey: "id",
    header: "ID",
    enableHiding: true,
  },
  {
    accessorKey: "created_by",
    header: "Usuário",
  },
  {
    accessorKey: "size",
    header: "Tamanho",
    cell: ({ row }) => {
      const bytes = row.getValue("size") as number;
      const mb = (bytes / (1024 * 1024)).toFixed(2);
      return `${mb} MB`;
    },
  },
  {
    accessorKey: "real_name",
    header: "Nome do arquivo",
  },
  {
    accessorKey: "doc_type",
    header: "Tipo do documento",
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const status = row.getValue("status") as string;
      const statusColors = {
        success: "text-green-600",
        error: "text-red-600",
        processing: "text-yellow-600",
      };
      return (
        <span
          className={
            statusColors[status as keyof typeof statusColors] || "text-gray-600"
          }
        >
          {status}
        </span>
      );
    },
  },
  {
    accessorKey: "error",
    header: "Mensagem",
  },
];

export function UploadPage(uploadConfig?: UploadConfig) {
  const { title, subtitle, docType, extensionFile, uploadTitle } =
    uploadConfig || {
      title: "",
      subtitle: "",
      docType: "",
      extensionFile: "",
      uploadTitle: "",
    };

  const datetime = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const { data: fileStatusData = [], isLoading } = useFileStatus(
    docType,
    datetime,
    !!docType
  );

  const stats = useMemo(() => {
    const totalFiles = fileStatusData.length;
    const errorCount = fileStatusData.filter((item) => item.status === "error").length;
    const successCount = fileStatusData.filter((item) => item.status === "success").length;

    return {
      totalFiles,
      errorCount,
      successCount,
      errorPercentage: totalFiles > 0 ? errorCount / totalFiles : 0,
      successPercentage: totalFiles > 0 ? successCount / totalFiles : 0,
    };
  }, [fileStatusData]);

  if (!uploadConfig) {
    return (
      <AnimatedPage className="p-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive mb-4">
            Configuração de Upload não encontrada
          </h1>
          <p className="text-muted-foreground">
            Esta página deve ser acessada através do menu lateral.
          </p>
        </div>
      </AnimatedPage>
    );
  }

  return (
    <AnimatedPage className="p-6">
      <FadeIn className="mb-6">
        <h1 className="text-3xl font-bold text-foreground">{title}</h1>
        <p className="text-muted-foreground mt-1">{subtitle}</p>
      </FadeIn>

      {isLoading ? (
        <StatBoxSkeleton count={4} />
      ) : (
        <StaggerChildren staggerDelay={0.05}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <FadeIn>
              <UploadBox
                title="Upload"
                docType={docType}
                allowedExtensions={extensionFile}
                icon={<Cloud className="h-6 w-6" />}
              />
            </FadeIn>

            <FadeIn delay={0.05}>
              <StatBox
                title="Total de arquivos recebidos hoje"
                value={stats.totalFiles}
                icon={<Upload className="h-6 w-6 text-primary" />}
              />
            </FadeIn>

            <FadeIn delay={0.1}>
              <ProgressCircle
                title="Arquivos com Erro"
                value={stats.errorCount}
                percentage={stats.errorPercentage}
                icon={<X className="h-6 w-6 text-destructive" />}
              />
            </FadeIn>

            <FadeIn delay={0.15}>
              <ProgressCircle
                title="Arquivos processados"
                value={stats.successCount}
                percentage={stats.successPercentage}
                icon={<Check className="h-6 w-6 text-green-600" />}
              />
            </FadeIn>
          </div>
        </StaggerChildren>
      )}

      {fileStatusData.length > 0 && (
        <FadeIn delay={0.2}>
          <div>
            <h2 className="text-xl font-semibold mb-4">Status dos Arquivos</h2>
            <DataTable
              columns={columns}
              data={fileStatusData}
              searchableColumns={["real_name"]}
              searchPlaceholder="Buscar por nome do arquivo..."
              loading={isLoading}
            />
          </div>
        </FadeIn>
      )}

      {!isLoading && fileStatusData.length === 0 && (
        <FadeIn delay={0.2}>
          <div className="text-center py-8">
            <p className="text-muted-foreground">
              Nenhum arquivo processado hoje para este tipo de documento.
            </p>
          </div>
        </FadeIn>
      )}
    </AnimatedPage>
  );
}
